import { useEffect, useState } from "react";
import CloseIcon from "../../assets/CloseIcon.svg";
import {
  GET_SELLER_BOX,
  GET_SELLER_ORDER_COMPLETE_DATA,
} from "../../utils/ApiUrls";
import { POST } from "../../utils/webService";

type DraftOrderProductModalProps = {
  order: any;
  onClose: () => void;
  isOpen?: boolean;
};

const emptyValue = "-";

const getOrderTempId = (order: any) => {
  if (order?.tempOrderId) return String(order.tempOrderId);
  if (order?.orderId && String(order.orderId).startsWith("T")) {
    return String(order.orderId).split("T")[1];
  }
  return "";
};

const formatValue = (value: any) => {
  if (value === null || value === undefined || value === "") return emptyValue;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Number.isFinite(value)) return String(value);
  return String(value);
};

const getNumber = (value: any) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const PANEL_SOURCES = [
  "WEBSITE_SINGLE_PAGE",
  "WEBSITE",
  "WEBSITE_ALPHA",
  "BULK_B2C",
  "MANUAL_BULK_B2C",
  "BULK_B2B",
  "MANUAL_BULK_B2B",
];

const isPanelSource = (source: string): boolean => {
  return PANEL_SOURCES.includes(source);
};

const calculateProductTotalPrice = (product: any) => {
  return getNumber(product?.qty) * getNumber(product?.unitPrice);
};

const calculateProductTotalWeight = (product: any) =>
  getNumber(product?.qty) * getNumber(product?.deadWeight);

const getBoxInvoiceValue = (box: any, source: string) => {
  if (isPanelSource(source)) {
    if (!box?.products || box.products.length === 0) return 0;
    return box.products.reduce((total: number, product: any) => {
      return total + calculateProductTotalPrice(product);
    }, 0);
  }

  return getNumber(box?.codInfo?.invoiceValue);
};

const calculateBoxTotalPrice = (box: any, order: any) => {
  const basePrice = getBoxInvoiceValue(box, order?.source || "");

  if (order?.orderType === "B2B") {
    return basePrice * getNumber(box?.qty || 1);
  }

  return basePrice;
};

const ReadOnlyField = ({
  label,
  value,
  className = "",
  required = false,
}: {
  label: string;
  value: any;
  className?: string;
  required?: boolean;
}) => {
  return (
    <div className={`relative min-w-0 ${className}`}>
      <div className="min-h-[58px] rounded-md bg-[#F8FAFC] px-4 pb-2 pt-5">
        <span className="absolute left-4 top-0 max-w-[calc(100%-32px)] -translate-y-1/2 bg-white px-1 text-xs font-medium leading-4 text-[#596579]">
          {label}
          {/* {required && <span className="text-red-500"> *</span>} */}
        </span>
        <p className="break-words text-sm font-semibold leading-5 text-[#1C1C1C]">
          {formatValue(value)}
        </p>
      </div>
    </div>
  );
};

const DraftOrderProductModal = ({
  order,
  onClose,
  isOpen,
}: DraftOrderProductModalProps) => {
  const [completeOrder, setCompleteOrder] = useState<any>(order || {});
  const [sellerBoxes, setSellerBoxes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    const tempOrderId = getOrderTempId(order);
    if (!tempOrderId) {
      setCompleteOrder(order || {});
      setErrorMessage("Unable to find temp order id for this order.");
      return;
    }

    let isActive = true;

    const fetchModalData = async () => {
      try {
        setIsLoading(true);
        setErrorMessage("");
        setCompleteOrder(order || {});

        const [orderResponse, boxResponse] = await Promise.all([
          POST(GET_SELLER_ORDER_COMPLETE_DATA, {
            tempOrderId,
            awb: order?.awb || "0",
          }),
          POST(GET_SELLER_BOX, {}),
        ]);

        if (!isActive) return;

        const orderData = orderResponse?.data;
        if (orderData?.status) {
          const fetchedOrder =
            orderData?.data?.[0]?.data?.[0] ||
            orderData?.data?.[0] ||
            order ||
            {};
          setCompleteOrder(fetchedOrder);
        } else {
          setErrorMessage(
            orderData?.message || "Unable to load complete order details.",
          );
        }

        const boxData = boxResponse?.data;
        if (boxData?.success || boxData?.status) {
          const fetchedBoxes = Array.isArray(boxData?.data)
            ? boxData.data
            : Array.isArray(boxData?.data?.[0])
              ? boxData.data[0]
              : [];
          setSellerBoxes(fetchedBoxes);
        }
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to load draft order product details:", error);
        setErrorMessage("Failed to load complete order and box details.");
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchModalData();

    return () => {
      isActive = false;
    };
  }, [isOpen, order]);

  const selectedOrder = completeOrder || order || {};
  const boxes = Array.isArray(selectedOrder?.boxInfo)
    ? selectedOrder.boxInfo
    : [];
  const findSellerBox = (box: any) => {
    if (!box || !sellerBoxes.length) return null;

    return sellerBoxes.find((sellerBox: any) => {
      const sameBoxId =
        box?.boxId &&
        sellerBox?.boxId &&
        String(box.boxId) === String(sellerBox.boxId);
      const sameName =
        box?.name &&
        sellerBox?.name &&
        String(box.name).toLowerCase() === String(sellerBox.name).toLowerCase();

      return sameBoxId || sameName;
    });
  };

  return (
    <div className="flex max-h-[90vh] w-full flex-col bg-white">
      <div className="flex flex-row items-center justify-between px-5 py-4 border bottom-1">
        <div className="text-lg font-semibold text-[#1C1C1C]">
          Order Details
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200"
          aria-label="Close product details"
        >
          <img src={CloseIcon} alt="" className="h-4 w-4 opacity-80" />
        </button>
      </div>

      <div className="customScroll overflow-y-auto px-4 py-5">
        <div className="w-full">
          {isLoading && (
            <div className="rounded-md border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-[#004EFF]">
              Loading complete order and box details...
            </div>
          )}

          {!isLoading && errorMessage && (
            <div className="rounded-md border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
              {errorMessage}
            </div>
          )}

          <section className={isLoading || errorMessage ? "mt-4" : ""}>
            <div className="mb-5 bg-white">
              <div className="px-1 py-2">
                <h3 className="text-base font-semibold text-[#1C1C1C]">
                  Box & Products
                </h3>
              </div>
            </div>

            {!boxes.length ? (
              <div className="rounded-md bg-[#F8FAFC] px-4 py-8 text-center text-sm text-gray-500">
                No box or product details available.
              </div>
            ) : (
              <div className="space-y-6">
                {boxes.map((box: any, boxIndex: number) => {
                  const sellerBox = findSellerBox(box);
                  const boxDetail = { ...(sellerBox || {}), ...box };
                  const products = Array.isArray(boxDetail?.products)
                    ? boxDetail.products
                    : [];

                  return (
                    <div
                      key={`${boxDetail?.boxId || boxDetail?.name || "box"}-${boxIndex}`}
                      className="bg-white"
                    >
                      <div className="mb-5 flex items-center justify-between bg-[#F8FAFC] px-5 py-4">
                        <h4 className="text-base font-semibold text-[#1C1C1C]">
                          Box {boxIndex + 1}: {boxDetail?.name || "Unnamed Box"}
                        </h4>
                      </div>

                      <div className="space-y-5">
                        <ReadOnlyField
                          label="Box Name"
                          value={boxDetail?.name}
                          required
                        />

                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                          <ReadOnlyField
                            label="Weight (kg)"
                            value={boxDetail?.deadWeight}
                          />
                          <ReadOnlyField
                            label="L (cm)"
                            value={boxDetail?.length}
                            required
                          />
                          <ReadOnlyField
                            label="B (cm)"
                            value={boxDetail?.breadth}
                            required
                          />
                          <ReadOnlyField
                            label="H (cm)"
                            value={boxDetail?.height}
                            required
                          />
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <ReadOnlyField
                            label="Box Quantity"
                            value={boxDetail?.qty || 1}
                            required
                          />
                          <ReadOnlyField
                            label="Box Total Price (₹)"
                            value={calculateBoxTotalPrice(
                              boxDetail,
                              selectedOrder,
                            ).toFixed(2)}
                          />
                        </div>

                        {products.length ? (
                          <div className="space-y-4">
                            {products.map(
                              (product: any, productIndex: number) => (
                                <div
                                  key={`product-${boxIndex}-${productIndex}`}
                                  className="bg-white"
                                >
                                  <div className="mb-5 flex items-center justify-between bg-[#EEF4FF] px-4 py-3">
                                    <h5 className="text-sm font-semibold text-[#1C1C1C]">
                                      Product {productIndex + 1}:{" "}
                                      {product?.name || "Unnamed Product"}
                                    </h5>
                                  </div>

                                  <div className="space-y-5">
                                    <ReadOnlyField
                                      label="Product Name"
                                      value={product?.name}
                                      required
                                    />

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                      <ReadOnlyField
                                        label="Product Qty"
                                        value={product?.qty}
                                        required
                                      />
                                      <ReadOnlyField
                                        label="Unit Weight"
                                        value={product?.deadWeight}
                                        required
                                      />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                                      <ReadOnlyField
                                        label="Unit Price"
                                        value={product?.unitPrice}
                                      />
                                      <ReadOnlyField
                                        label="L(cm)"
                                        value={product?.length}
                                      />
                                      <ReadOnlyField
                                        label="B(cm)"
                                        value={product?.breadth}
                                      />
                                      <ReadOnlyField
                                        label="H(cm)"
                                        value={product?.height}
                                      />
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                      <ReadOnlyField
                                        label="Total Weight (kg)"
                                        value={calculateProductTotalWeight(
                                          product,
                                        ).toFixed(2)}
                                      />
                                      <ReadOnlyField
                                        label="Product Total Price"
                                        value={calculateProductTotalPrice(
                                          product,
                                        ).toFixed(2)}
                                      />
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                      <ReadOnlyField
                                        label="SKU"
                                        value={product?.sku}
                                      />
                                      <ReadOnlyField
                                        label="HSN"
                                        value={product?.hsnCode}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        ) : (
                          <div className="rounded-md bg-[#F8FAFC] px-4 py-5 text-center text-sm text-gray-500">
                            No product details available for this box.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default DraftOrderProductModal;
